import {useNuxtApp} from 'nuxt/app'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import {useCartStore} from '@/store/cart'
import {usePlantStore} from '@/store/plants'
import {useCustomerSettingsStore} from '@/store/customer-settings'
import {useCustomerService} from '@/composables/customer_service'
import {useKeycloakService} from '~/composables/keycloak_service'
import type {UserManager} from 'oidc-client-ts'
import {getRegionForSalesArea} from '~/assets/js/utils/region_service'
import {DefaultCustomerUuid, MAX_CUSTOMERS, TOKEN} from '~/assets/js/constants'
import {isTokenExpired} from '~/assets/js/authentication'
import {useGtm} from '@gtm-support/vue-gtm'
import * as Sentry from '@sentry/browser'
import {decodeKeycloakToken, restoreUserStoreFromAccessToken, buildKeycloakUser, setImpersonationToken, getImpersonationToken, isImpersonationTokenValid} from './useKeycloakToken'
import {clearKeycloakState, refreshKeycloakSession} from './useKeycloakSession'

async function processKeycloakUserData(
  accessToken: string,
  gtmAction: string = 'login'
) {
  const app = useNuxtApp()
  const userStore = useUserStore()
  const customerService = useCustomerService()

  const decodedToken = decodeKeycloakToken(accessToken)

  const region = app.$globalization.getRegionFromURL()
  const customerRegion = decodedToken.salesAreaName
    ? getRegionForSalesArea(decodedToken.salesAreaName.replaceAll('_', '-'))
    : null
  app.$globalization.setRegion(customerRegion)

  if (region && customerRegion && region !== customerRegion) {
    const targetUrl = app.$globalization.getRegionPath(customerRegion)
    globalThis.location.replace(targetUrl)
    return
  }

  userStore.fetchUserData()
  userStore.parseUserRights()
  userStore.parseExcludedBrands()
  userStore.parseIncludedBrands()

  const defaultcustomerUuid = decodedToken.iss
  const tokenScope = decodedToken.scope ?? ''

  app.$gtmNewSession?.()
  useGtm()?.trackEvent({
    event: 'login',
    category: 'login',
    action: gtmAction,
    companyNo: decodedToken?.companyNo,
    salesAreaName: decodedToken?.salesAreaName,
    userType: decodedToken?.userIsInternal === 'true' ? 'internal' : 'external',
    authProvider: 'keycloak'
  })

  if (defaultcustomerUuid && defaultcustomerUuid !== DefaultCustomerUuid) {
    userStore.companiesLoading = true
    let defaultCustomer = null
    if (tokenScope.split(' ').includes(`https://sso.fst.com/${defaultcustomerUuid}/Customer.View`)) {
      defaultCustomer = await customerService.getCustomer(defaultcustomerUuid.split('/')[3], accessToken)
    } else {
      defaultCustomer = await customerService.getBasicCustomer(defaultcustomerUuid.split('/')[3], accessToken)
    }
    await userStore.setSelectedCustomer(defaultCustomer?.value.find(Boolean))
    const userCustomerSettingsStore = useCustomerSettingsStore()
    await userCustomerSettingsStore.fetchCustomerSettings()
    customerService.getFilteredCustomerList(undefined, MAX_CUSTOMERS).then((companies) => {
      userStore.setAvailableCompanies(companies.count)
      userStore.setCompanies(companies.value)
      userStore.companiesLoading = false
    })
    usePlantStore().fetchAllPlants(false)
  }

  const cartStore = useCartStore()
  cartStore.fetchCartCount()
  cartStore.fetchCertificates()
}

async function fetchKeycloakUser(userManager: UserManager) {
  const userStore = useUserStore()
  const authStore = useAuthStore()
  authStore.userFetchInProgress = true

  const app = useNuxtApp()
  try {
    if (userStore.customerUuidLink && userStore.customerUuidLink !== 'default') {
      const customerUuid = userStore.customerUuidLink
      // @ts-ignore - userStore.newCustomer can accept string
      userStore.newCustomer = customerUuid
      userStore.customerUuidLink = ''
      await switchKeycloakCustomer(customerUuid, 'login')
      return
    }
    userStore.customerUuidLink = ''

    const accessToken = userStore.accessToken
    if (!accessToken) {
      Sentry.captureMessage('No access token available for Keycloak user fetch', 'warning')
      return
    }

    await processKeycloakUserData(accessToken, 'login')
  } catch (e: any) {
    if (e?.response?.data?.errorCode === 'Customer_Uuid_NotExisting' || e?.response?.data?.errorCode === 'Unauthorized') {
      await clearKeycloakState(userManager)
      const baseUrl = useRuntimeConfig().public.BASE_URL
      const accessDeniedPath = baseUrl + app.localePath('/access-denied', null, null)
      globalThis.location.replace(accessDeniedPath)
      return
    }

    await clearKeycloakState(userManager)
    Sentry.captureException(e, {tags: {source: 'keycloak_user_fetch'}})
  } finally {
    authStore.userFetchInProgress = false
  }
}

async function switchKeycloakCustomer(newCustomerUuid: string, gtmAction: string = 'change_company') {
  if (!process.client) {return}

  const app = useNuxtApp()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const {switchKeycloakCustomer: backendSwitch, impersonateKeycloakUser: backendImpersonate} = useKeycloakService()

  authStore.userFetchInProgress = true

  try {
    const impersonationToken = getImpersonationToken()
    if (impersonationToken && isImpersonationTokenValid(impersonationToken)) {
      const targetEmail = decodeKeycloakToken(impersonationToken).email
      const adminUser = authStore.userObject as any
      const adminAccessToken = adminUser?.access_token
      if (!targetEmail || !adminAccessToken) {
        Sentry.captureMessage('Missing target email or admin token for impersonated customer switch', 'warning')
        return
      }

      const res = await backendImpersonate(newCustomerUuid, adminAccessToken, targetEmail)
      if (res?.data?.accessToken) {
        const newAccessToken = res.data.accessToken
        setImpersonationToken(newAccessToken)
        restoreUserStoreFromAccessToken(newAccessToken)
        await processKeycloakUserData(newAccessToken, gtmAction)
      }
      return
    }

    let currentAccessToken = userStore.accessToken
    if (!currentAccessToken) {
      Sentry.captureMessage('No access token available for customer switch', 'warning')
      return
    }

    // gate: refresh here explicitly
    if (isTokenExpired(currentAccessToken, TOKEN.EXPIRE_BUFFER)) {
      const userForRefresh = await app.$userManager.getUser()
      if (userForRefresh?.refresh_token) {
        currentAccessToken = await refreshKeycloakSession(userForRefresh) ?? currentAccessToken
      }
    }

    // Call backend to get new token with new customer context
    const res = await backendSwitch(newCustomerUuid, currentAccessToken)

    if (res?.data?.accessToken) {
      const newAccessToken = res.data.accessToken

      restoreUserStoreFromAccessToken(newAccessToken)

      const existingUser = await app.$userManager.getUser()
      const updatedUser = buildKeycloakUser(
        newAccessToken,
        res.data.idToken ?? existingUser?.id_token,
        res.data.refreshToken ?? existingUser?.refresh_token,
        existingUser?.state
      )

      await app.$userManager.storeUser(updatedUser)
      authStore.setUserObject(updatedUser)

      await processKeycloakUserData(newAccessToken, gtmAction)
    }
  } catch (error: any) {
    Sentry.captureException(error, {tags: {source: 'keycloak_customer_switch'}})
    throw error
  } finally {
    authStore.userFetchInProgress = false
  }
}

async function impersonateKeycloakUser(targetEmail: string, gtmAction: string = 'impersonation') {
  if (!process.client) {return}

  const app = useNuxtApp()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const {impersonateKeycloakUser: backendImpersonate} = useKeycloakService()

  authStore.userFetchInProgress = true

  try {
    const adminUser = authStore.userObject as any
    const adminAccessToken = adminUser?.access_token || userStore.accessToken
    if (!adminAccessToken) {
      Sentry.captureMessage('No admin access token available for impersonation', 'warning')
      return
    }

    // Re-enrich the admin's enriched token with the target user.
    const res = await backendImpersonate(null, adminAccessToken, targetEmail)

    if (res?.data?.accessToken) {
      const newAccessToken = res.data.accessToken

      // Persist the impersonated token per-tab (sessionStorage). This makes impersonation survive
      // navigation AND refresh within THIS tab without keeping ?targetEmail= in the URL, while the
      // admin's durable session (authStore.userObject / userManager / shared localStorage) stays
      // untouched - so impersonation can never leak into the admin's other tabs and ends when this
      // tab is closed.
      setImpersonationToken(newAccessToken)
      restoreUserStoreFromAccessToken(newAccessToken)

      await processKeycloakUserData(newAccessToken, gtmAction)
    }
  } catch (error: any) {
    const errorCode = error?.response?.data?.errorCode
    if (errorCode === 'Impersonation_NotAllowed' || errorCode === 'User_Uuid_NotExisting' || error?.response?.status === 403) {
      const baseUrl = useRuntimeConfig().public.BASE_URL
      const accessDeniedPath = baseUrl + app.localePath('/access-denied', null, null)
      globalThis.location.replace(accessDeniedPath)
      return
    }
    Sentry.captureException(error, {tags: {source: 'keycloak_impersonation'}})
    throw error
  } finally {
    authStore.userFetchInProgress = false
  }
}

// Re-apply an existing per-tab impersonation session (from sessionStorage) into the runtime user
// store on a fresh page load.
async function applyKeycloakImpersonation(accessToken: string, gtmAction: string = 'impersonation') {
  if (!process.client) {return}

  restoreUserStoreFromAccessToken(accessToken)
  await processKeycloakUserData(accessToken, gtmAction)
}

export const useKeycloakUser = () => {
  return {
    fetchKeycloakUser,
    switchKeycloakCustomer,
    impersonateKeycloakUser,
    applyKeycloakImpersonation
  }
}
